import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export interface MentorshipRelationship {
  id: string;
  thread_id: string | null;
  mentor_id: string;
  entrepreneur_id: string;
  status: "active" | "completed";
  started_at: string;
  completed_at: string | null;
  completed_by: string | null;
  completion_note: string | null;
  created_at: string;
  updated_at: string;
  mentor?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
    role: string;
  };
  entrepreneur?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
    role: string;
  };
}

interface ConnectionWithProfiles {
  id: string;
  thread_id: string | null;
  requester_id: string;
  recipient_id: string;
  connection_type: string;
  status: string;
  is_mentorship_active: boolean;
  mentorship_completed_at: string | null;
  mentorship_completion_note: string | null;
  created_at: string;
  updated_at: string;
  accepted_at: string | null;
  requester: {
    id: string;
    full_name: string;
    avatar_url: string | null;
    role: string;
  } | null;
  recipient: {
    id: string;
    full_name: string;
    avatar_url: string | null;
    role: string;
  } | null;
}

// Fetch all mentorship relationships for current user from the unified connections table
export function useMentorshipRelationships() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["mentorship-relationships", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      console.log("[useMentorshipRelationships] Fetching mentorship connections for user:", user.id);

      const { data, error } = await supabase
        .from("connections")
        .select(`
          id,
          thread_id,
          requester_id,
          recipient_id,
          connection_type,
          status,
          is_mentorship_active,
          mentorship_completed_at,
          mentorship_completion_note,
          created_at,
          updated_at,
          accepted_at,
          requester:profiles!connections_requester_id_fkey(id, full_name, avatar_url, role),
          recipient:profiles!connections_recipient_id_fkey(id, full_name, avatar_url, role)
        `)
        .eq("connection_type", "mentorship")
        .eq("status", "accepted")
        .or(`requester_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .order("accepted_at", { ascending: false, nullsFirst: false });

      if (error) {
        console.error("[useMentorshipRelationships] Query error:", error.message, error.code);
        throw error;
      }

      console.log("[useMentorshipRelationships] Query successful, found connections:", data?.length || 0);

      // Transform connections to MentorshipRelationship format
      return (data || []).map((conn): MentorshipRelationship => {
        const rawConn = conn as unknown as ConnectionWithProfiles;
        
        // Handle potential array responses from Supabase joins
        const requesterProfile = Array.isArray(rawConn.requester) ? rawConn.requester[0] : rawConn.requester;
        const recipientProfile = Array.isArray(rawConn.recipient) ? rawConn.recipient[0] : rawConn.recipient;

        // Determine who is mentor vs entrepreneur based on profile roles
        let mentor: MentorshipRelationship["mentor"];
        let entrepreneur: MentorshipRelationship["entrepreneur"];
        let mentorId: string;
        let entrepreneurId: string;

        if (requesterProfile?.role === "mentor") {
          mentor = requesterProfile;
          mentorId = rawConn.requester_id;
          entrepreneur = recipientProfile || undefined;
          entrepreneurId = rawConn.recipient_id;
        } else if (recipientProfile?.role === "mentor") {
          mentor = recipientProfile;
          mentorId = rawConn.recipient_id;
          entrepreneur = requesterProfile || undefined;
          entrepreneurId = rawConn.requester_id;
        } else {
          // Fallback: assume requester is entrepreneur, recipient is mentor
          // (typical flow: entrepreneur requests mentorship from mentor)
          mentor = recipientProfile || undefined;
          mentorId = rawConn.recipient_id;
          entrepreneur = requesterProfile || undefined;
          entrepreneurId = rawConn.requester_id;
        }

        return {
          id: rawConn.id,
          thread_id: rawConn.thread_id,
          mentor_id: mentorId,
          entrepreneur_id: entrepreneurId,
          status: rawConn.is_mentorship_active ? "active" : "completed",
          started_at: rawConn.accepted_at || rawConn.created_at,
          completed_at: rawConn.mentorship_completed_at,
          completed_by: null, // Not tracked in connections table
          completion_note: rawConn.mentorship_completion_note,
          created_at: rawConn.created_at,
          updated_at: rawConn.updated_at,
          mentor,
          entrepreneur,
        };
      });
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// Get active mentorship relationships (Current Mentorship Relationships)
export function useActiveMentorshipRelationships() {
  const { data: relationships, ...rest } = useMentorshipRelationships();
  
  const activeRelationships = relationships?.filter(r => r.status === "active") || [];
  
  return { data: activeRelationships, ...rest };
}

// Get completed mentorship relationships (Past Requests)
export function useCompletedMentorshipRelationships() {
  const { data: relationships, ...rest } = useMentorshipRelationships();
  
  const completedRelationships = relationships?.filter(r => r.status === "completed") || [];
  
  return { data: completedRelationships, ...rest };
}

// Complete a mentorship relationship
export function useCompleteMentorshipRelationship() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      threadId,
      note,
    }: {
      threadId: string;
      note?: string;
    }) => {
      // Find the connection by thread_id and update it
      const { data: connection, error: findError } = await supabase
        .from("connections")
        .select("id")
        .eq("thread_id", threadId)
        .eq("connection_type", "mentorship")
        .single();

      if (findError || !connection) {
        throw new Error("Mentorship connection not found");
      }

      const { error } = await supabase
        .from("connections")
        .update({
          is_mentorship_active: false,
          mentorship_completed_at: new Date().toISOString(),
          mentorship_completion_note: note || null,
        })
        .eq("id", connection.id);

      if (error) throw error;

      return { success: true, connection_id: connection.id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mentorship-relationships"] });
      queryClient.invalidateQueries({ queryKey: ["message-threads"] });
      queryClient.invalidateQueries({ queryKey: ["unified_connections"] });
      toast.success("Mentorship completed — connection remains active.");
    },
    onError: (error) => {
      toast.error("Failed to complete mentorship: " + error.message);
    },
  });
}

// Request mentorship again (restart from Past Requests)
export function useRequestMentorshipAgain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (threadId: string) => {
      // Find the connection by thread_id
      const { data: connection, error: findError } = await supabase
        .from("connections")
        .select("id, is_mentorship_active")
        .eq("thread_id", threadId)
        .eq("connection_type", "mentorship")
        .single();

      if (findError || !connection) {
        throw new Error("Mentorship connection not found");
      }

      if (connection.is_mentorship_active) {
        throw new Error("already_active");
      }

      const { error } = await supabase
        .from("connections")
        .update({
          is_mentorship_active: true,
          mentorship_completed_at: null,
          mentorship_completion_note: null,
        })
        .eq("id", connection.id);

      if (error) throw error;

      return { success: true, connection_id: connection.id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mentorship-relationships"] });
      queryClient.invalidateQueries({ queryKey: ["message-threads"] });
      queryClient.invalidateQueries({ queryKey: ["unified_connections"] });
      toast.success("Mentorship request restarted.");
    },
    onError: (error) => {
      if (error.message === "already_active") {
        toast.info("Mentorship is already active.");
      } else {
        toast.error("Failed to restart mentorship: " + error.message);
      }
    },
  });
}

// Initialize mentorship relationship when accepting a request
export function useInitializeMentorshipRelationship() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      threadId,
      mentorId,
      entrepreneurId,
    }: {
      threadId: string;
      mentorId: string;
      entrepreneurId: string;
    }) => {
      // Check if connection already exists
      const { data: existing } = await supabase
        .from("connections")
        .select("id")
        .eq("connection_type", "mentorship")
        .or(`and(requester_id.eq.${mentorId},recipient_id.eq.${entrepreneurId}),and(requester_id.eq.${entrepreneurId},recipient_id.eq.${mentorId})`)
        .single();

      if (existing) {
        // Update existing to be active
        const { error } = await supabase
          .from("connections")
          .update({
            is_mentorship_active: true,
            thread_id: threadId,
          })
          .eq("id", existing.id);

        if (error) throw error;
        return existing.id;
      }

      // Create new mentorship connection
      const { data, error } = await supabase
        .from("connections")
        .insert({
          requester_id: entrepreneurId,
          recipient_id: mentorId,
          connection_type: "mentorship",
          status: "accepted",
          is_mentorship_active: true,
          thread_id: threadId,
          accepted_at: new Date().toISOString(),
        })
        .select("id")
        .single();

      if (error) throw error;
      return data.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mentorship-relationships"] });
      queryClient.invalidateQueries({ queryKey: ["unified_connections"] });
    },
  });
}
